import { memo, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PaymentMethodsEnum } from "@zenith/features/Payments/types";
import StockPrecheckErrorModal, {
  parseStockError,
  parseStockErrorFromApiData,
  parseStockErrorItems,
  StockApiDataItem,
  StockErrorItem,
} from "@zenith/features/CollectPayment/components/stock-precheck-error-modal/stock-precheck-error-modal";

import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";

import { Copy, USSD } from "@zenith/assets/icons/components";
import { AutocompleteComponent, ButtonComponent } from "@zenith/components";
import { AutocompleteSelectItemsType } from "@zenith/components/ui/AutocompleteComponent/autocomplete-component/interfaces";
import { usePaymentsContext } from "@zenith/context/global-context/hooks";
import {
  DEFAULT_CHANNEL,
  DEFAULT_CURRENCY,
  EXPIRATION_DATE_FOR_PAYMENTS,
  EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM,
  EXPIRATION_DATE_FOR_USSD_CODE_ITEM,
  MILLISECONDS,
  STOCK_PRECHECK_ERROR_CODE,
} from "@zenith/data/constants";
import {
  PAYMENT_OVERVIEW,
  PAYMENT_USSD_CODE,
  PAYMENT_USSD_CODE_PENDING,
} from "@zenith/routes/paths";
import {
  ApiResponseDTOGenerateDTO,
  ApiResponseDTOOperationDTO,
  BankUssdRecord,
  GenerateApiService,
  OperationDTO,
  OperationsApiService,
  UssdApiService,
} from "@zenith/services/transactions";
import { formatCurrency, formatTimeForCounter } from "@zenith/utils";
import { pollPaymentStatus } from "@zenith/utils/Polls/pollPaymentStatus";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

interface DisableState {
  generateButton: boolean;
  confirmButton: boolean;
}

const USSDCodePage = memo(function USSDCodePage() {
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const { paymentsContext, setPaymentsContext } = usePaymentsContext();
  const {
    totalAmount,
    expirationDate,
    operationId,
    amount,
    transactionInfo = { ussdCode: undefined, bankInfo: undefined },
  } = paymentsContext;

  const { ussdCode } = transactionInfo;
  const navigate = useNavigate();
  const location = useLocation();

  const [copyFeedback, setCopyFeedback] = useState<string>("");
  const [error, setError] = useState("");
  const [disabledStateObject, setDisabledState] = useState<DisableState>({
    generateButton: true,
    confirmButton: true,
  });
  const [timer, setTimer] = useState<NodeJS.Timer>();
  const [seconds, setSeconds] = useState<number>(0);
  const [bankSelected, setBankSelected] = useState<BankUssdRecord>();
  const [label, setLabel] = useState<string>("");
  const [isUSSDCode, setUSSDCode] = useState<boolean>(false);
  const [bankList, setBankList] = useState<AutocompleteSelectItemsType[]>([]);
  const [USSDBankList, setUSSDBankList] = useState<Array<BankUssdRecord>>([]);
  const [isLoadingBanks, setLoadingBanks] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showStockErrorModal, setShowStockErrorModal] = useState(false);
  const [stockErrorItems, setStockErrorItems] = useState<StockErrorItem[]>([]);

  useEffect(() => {
    if (expirationDate) {
      const initialRemainingSeconds = Math.floor(
        (new Date(expirationDate).getTime() - new Date().getTime()) /
          MILLISECONDS
      );
      setSeconds(initialRemainingSeconds);

      const interval = setInterval(() => {
        setSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [expirationDate]);

  const mapGenerateToOperationResponse = (
    response: ApiResponseDTOGenerateDTO
  ): ApiResponseDTOOperationDTO => {
    return {
      data: {
        operationId: response.data?.operationCode,
        status: OperationDTO.status.PENDING,
        ussd: {
          operationCode: response.data?.operationCode,
          expirationDate: response.data?.expirationDate,
        },
        currency: "",
        createdAt: response.dateTime || "",
        operationType: OperationDTO.operationType.IN,
        paymentMethod: OperationDTO.paymentMethod.USSD,
      },
      dateTime: response.dateTime,
      internalCode: response.internalCode,
      message: response.message,
      success: response.success,
    };
  };

  const handleUssdResponseError = (
    message: string | undefined,
    data?: StockApiDataItem[],
    internalCode?: string
  ) => {
    const errorMessage = message || "";
    if (internalCode === STOCK_PRECHECK_ERROR_CODE) {
      setStockErrorItems(
        errorMessage
          ? parseStockErrorItems(errorMessage, paymentsContext.orderItems ?? [])
          : []
      );
      setShowStockErrorModal(true);
    } else if (Array.isArray(data) && data.length > 0) {
      setStockErrorItems(
        parseStockErrorFromApiData(data, paymentsContext.orderItems ?? [])
      );
      setShowStockErrorModal(true);
    } else if (/Insufficient stock for product/i.test(errorMessage)) {
      setStockErrorItems(
        parseStockError(errorMessage, paymentsContext.orderItems ?? [])
      );
      setShowStockErrorModal(true);
    } else {
      setError(errorMessage || "An error occurred");
    }
  };

  const handleGenerateUSSD = async () => {
    setIsGeneratingCode(true);

    if (error) {
      setError("");
    }

    try {
      if (!bankSelected) {
        throw new Error("Please select a bank first");
      }

      if (!operationId) {
        const {
          saleItemDetail,
          amount: manualAmount,
          currency,
          description,
        } = location.state || {};

        if (saleItemDetail) {
          const response = await OperationsApiService.postOperationUssd(
            DEFAULT_CHANNEL,
            {
              currency: DEFAULT_CURRENCY,
              saleItemDetail,
              ussdBank: bankSelected,
            }
          );
          if (response?.data) {
            handleOperationResponse(response);
          } else {
            handleUssdResponseError(
              response?.message,
              undefined,
              response?.internalCode
            );
          }
        } else if (manualAmount && currency) {
          const response = await OperationsApiService.postOperationUssd1(
            DEFAULT_CHANNEL,
            {
              amount: manualAmount,
              currency,
              description: description || "",
              ussdBank: bankSelected,
            }
          );
          if (response?.data) {
            handleOperationResponse(response);
          } else {
            handleUssdResponseError(
              response?.message,
              undefined,
              response?.internalCode
            );
          }
        } else {
          throw new Error("Missing amount or sale item details");
        }
      } else {
        const generateResponse = await putGenerateUSSD();
        if (generateResponse?.data) {
          const mappedResponse =
            mapGenerateToOperationResponse(generateResponse);
          handleOperationResponse(mappedResponse);
        }
      }
    } catch (error: any) {
      const errorMessage: string = error?.body?.message || error?.message || "";
      handleUssdResponseError(
        errorMessage,
        error?.body?.data,
        error?.body?.internalCode
      );
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleOperationResponse = (response: ApiResponseDTOOperationDTO) => {
    if (response?.data) {
      const operationData = response.data;
      const expirationDate = operationData.ussd?.expirationDate;

      setPaymentsContext({
        ...paymentsContext,
        operationId: operationData.operationId || "",
        expirationDate: expirationDate || "",
        paymentMethod: PaymentMethodsEnum.USSD,
        transactionInfo: {
          ...paymentsContext.transactionInfo,
          transactionDate: response.dateTime || "",
          paymentMethod: OperationDTO.paymentMethod.USSD,
          ussdCode: operationData.ussd?.operationCode || "",
          bankInfo: bankSelected,
        },
      });

      localStorage.setItem(
        "ussd_state",
        JSON.stringify({
          operationId: operationData.operationId,
          expirationDate,
          paymentMethod: PaymentMethodsEnum.USSD,
          operationPaymentMethod: OperationDTO.paymentMethod.USSD,
          ussdCode: operationData.ussd?.operationCode,
          bankInfo: bankSelected,
          timestamp: Date.now(),
        })
      );

      if (expirationDate) {
        localStorage.setItem(
          EXPIRATION_DATE_FOR_USSD_CODE_ITEM,
          expirationDate
        );
      }

      setUSSDCode(true);
      setDisabledState({ generateButton: true, confirmButton: false });
      setIsSuccess(false);
    }
  };

  async function putGenerateUSSD() {
    if (!operationId || !bankSelected) {
      throw new Error("Missing required data for USSD generation");
    }

    setDisabledState((prev) => ({ ...prev, confirmButton: true }));

    return await GenerateApiService.putGenerateUssd(operationId, bankSelected);
  }

  const handleCopyClick = () => {
    const codeToCopy = ussdCode as string;
    const textArea = document.createElement("textarea");
    textArea.value = codeToCopy;
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const feedback = successful
        ? "Code copied to clipboard!"
        : "Copy failed.";
      setCopyFeedback(feedback);
    } catch (err) {
      console.error("Copy failed: ", err);
    }

    document.body.removeChild(textArea);
  };

  const handleConfirmPayment = async () => {
    if (seconds <= 0 || !operationId) return;

    setIsConfirming(true);
    try {
      const response = await pollPaymentStatus(operationId, false);

      setPaymentsContext({
        ...paymentsContext,
        ...response.data,
        paymentStatus: response.status,
        operationDetails: response.data, 
      });

      if (response.status === OperationDTO.status.SUCCESS) {
        localStorage.removeItem(EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM);
        localStorage.setItem("paymentStatus", OperationDTO.status.SUCCESS);

        navigate(PAYMENT_USSD_CODE_PENDING, {
          state: {
            prevPath: PAYMENT_USSD_CODE,
            initialStatus: OperationDTO.status.SUCCESS,
          },
        });
      } else {
        const expirationDateForUssd = Date.now() + EXPIRATION_DATE_FOR_PAYMENTS;
        localStorage.setItem(
          EXPIRATION_DATE_FOR_PENDING_USSD_CODE_ITEM,
          expirationDateForUssd.toString()
        );

        navigate(PAYMENT_USSD_CODE_PENDING, {
          state: {
            prevPath: PAYMENT_USSD_CODE,
            initialStatus: response.status,
            errorMessage: response.error?.message,
          },
        });
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
    } finally {
      setIsConfirming(false);
    }
  };

  useEffect(() => {
    if (!paymentsContext.totalAmount && location.state?.amount) {
      const amount = location.state.amount;
      setPaymentsContext({
        ...paymentsContext,
        totalAmount: amount,
      });
      localStorage.setItem("ussd_total_amount", amount.toString());
    }
  }, [paymentsContext.totalAmount, location.state?.amount, setPaymentsContext, paymentsContext]);

  useEffect(() => {
    const storedAmount = localStorage.getItem("ussd_total_amount");
    if (storedAmount && !paymentsContext.totalAmount) {
      setPaymentsContext({
        ...paymentsContext,
        totalAmount: parseFloat(storedAmount),
      });
    }
  }, []);

  function handleBankSelected(value: string, _label: string) {
    if (value) {
      const bank = USSDBankList.find((b) => b.bankCode === value);
      setDisabledState({ confirmButton: true, generateButton: false });
      setBankSelected(bank);

      setPaymentsContext({
        ...paymentsContext,
        operationId: "",
        paymentStatus: undefined,
        transactionInfo: {
          ...paymentsContext.transactionInfo,
          bankInfo: bank,
          ussdCode: undefined,
        },
      });
      setUSSDCode(false);

      if (bank) {
        handleInput(bank.bankName);
      }
    }
  }

  function handleInput(value: string) {
    setLabel(value);
  }

  function getBanksList() {
    setLoadingBanks(true);
    UssdApiService.getAvailableBanksUssd()
      .then((response) => {
        if (response && "data" in response) {
          if (response.success && response.data?.length) {
            const banks: AutocompleteSelectItemsType[] = response.data?.map(
              (bank) => {
                return {
                  label: bank.bankName,
                  value: bank.bankCode,
                };
              }
            );
            setUSSDBankList(response.data);
            setBankList(banks);
            if (paymentsContext.transactionInfo.bankInfo) {
              setBankSelected(paymentsContext.transactionInfo.bankInfo);
              setLabel(paymentsContext.transactionInfo.bankInfo.bankName);
            }

            if (paymentsContext.transactionInfo.ussdCode) {
              setUSSDCode(true);
              setDisabledState({ confirmButton: false, generateButton: true });
            }
          }
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingBanks(false));
  }

  useEffect(() => {
    const storedState = localStorage.getItem("ussd_state");
    if (storedState) {
      try {
        const {
          operationId: storedOpId,
          expirationDate: storedExpDate,
          paymentMethod,
          operationPaymentMethod,
          ussdCode: storedUssdCode,
          bankInfo,
          timestamp,
        } = JSON.parse(storedState);

        const now = Date.now();
        const expTime = new Date(storedExpDate).getTime();

        if (expTime > now) {
          setPaymentsContext({
            ...paymentsContext,
            operationId: storedOpId,
            expirationDate: storedExpDate,
            paymentMethod: paymentMethod as PaymentMethodsEnum,
            transactionInfo: {
              ...paymentsContext.transactionInfo,
              ussdCode: storedUssdCode,
              bankInfo,
              paymentMethod:
                operationPaymentMethod as OperationDTO.paymentMethod,
            },
          });

          if (bankInfo) {
            setBankSelected(bankInfo);
            setLabel(bankInfo.bankName);
          }

          setUSSDCode(true);
          setDisabledState({ generateButton: true, confirmButton: false });

          const remainingSeconds = Math.floor((expTime - now) / MILLISECONDS);
          setSeconds(remainingSeconds);
        } else {
          localStorage.removeItem("ussd_state");
        }
      } catch (e) {
        localStorage.removeItem("ussd_state");
      }
    }
  }, []);

  useEffect(() => {
    getBanksList();
    return () => {
      setIsSuccess(false);
      setBankList([]);
      setBankSelected({ bankCode: "", bankName: "", ussdCode: "" });
      setPaymentsContext({
        ...paymentsContext,
        paymentMethod: undefined,
        transactionInfo: {
          ...paymentsContext.transactionInfo,
          ussdCode: undefined,
          bankInfo: undefined,
          paymentMethod: undefined,
        },
      });
      setUSSDCode(false);
      clearInterval(timer);
      localStorage.removeItem("ussd_code_polling");
      localStorage.removeItem("ussd_state");
    };
  }, []);

  const handleBackButton = () => {
    setPaymentsContext({
      ...paymentsContext,
      transactionInfo: {
        ...paymentsContext.transactionInfo,
        ussdCode: undefined,
        bankInfo: undefined,
      },
    });
    navigate(PAYMENT_OVERVIEW, {
      state: { prevPath: PAYMENT_USSD_CODE },
    });
  };

  return (
    <div>
      <StockPrecheckErrorModal
        isOpen={showStockErrorModal}
        onClose={() => setShowStockErrorModal(false)}
        errorItems={stockErrorItems}
      />
      <ButtonComponent
        id="back-ussd-link"
        onClick={handleBackButton}
        label="Back"
        size="large"
        type="tertiary"
        iconStart={<ChevronLeft />}
      />

      <div className="mt-12 mb-6 flex justify-center">
        <div className="min-h-80 flex w-120 flex-col rounded-2.5xl border border-neutral-200 px-8 pt-6 pb-8">
          <h1 className="mb-4 text-2xl font-medium text-neutral-900">
            USSD
          </h1>
          <p className="mb-6 text-sm font-normal text-neutral-500">
            You will need to generate a USSD to be able to receive the
            payment
          </p>
          <hr className="mb-6 border-neutral-200"></hr>
          <div className="mb-8 flex justify-between">
            <h2 className="text-base font-normal text-neutral-400">Amount</h2>
            <h2 className="text-main-200-925 text-2xl font-medium">
              {paymentsContext.orderItems &&
              paymentsContext.orderItems.length === 0
                ? formatCurrency(amount)
                : formatCurrency(totalAmount)}
            </h2>
          </div>
          <div>
            <p className="mb-2 text-base font-normal text-black">
              Select a bank
            </p>
            <p className="text-sm font-normal text-neutral-500">
              Please select the bank of your paying customer
            </p>
            <div className="mt-8 flex w-full flex-row gap-x-2">
              <div className="w-56">
                <AutocompleteComponent
                  id="select-bank"
                  label=""
                  labelId="no-label-bank"
                  selectValue={
                    bankSelected
                      ? {
                          value: bankSelected.bankCode,
                          label: bankSelected.bankName,
                        }
                      : null
                  }
                  onChange={handleBankSelected}
                  onInputChange={handleInput}
                  options={bankList}
                  placeholder="Search by bank"
                  noOptionsText="No Banks Found"
                  showSearchIcon
                  isLoading={isLoadingBanks}
                  disabled={
                    isGeneratingCode ||
                    (isUSSDCode && !!ussdCode && seconds > 0)
                  }
                />
              </div>

              <ButtonComponent
                id="back-to-payment-overview"
                disabled={
                  disabledStateObject.generateButton || isGeneratingCode
                }
                onClick={handleGenerateUSSD}
                label="Generate Code"
                size="large"
                type="tertiary"
                isLoading={isGeneratingCode}
              />
            </div>
          </div>
          {isUSSDCode && ussdCode && seconds > 0 ? (
            <>
              <div className="h-17 mt-8 flex justify-center rounded-2xl border border-neutral-200 py-4 text-3.5xl font-medium text-neutral-900">
                {ussdCode}
              </div>
              <div className="my-6 flex flex-col items-center">
                <IconButton onClick={handleCopyClick}>
                  <Copy />
                </IconButton>
                <Button
                  onClick={handleCopyClick}
                  className="text-sm font-normal normal-case text-neutral-600"
                >
                  Copy
                </Button>
              </div>
            </>
          ) : (
            isUSSDCode && (
              <div className="mt-8 flex h-[258px] flex-col items-center justify-center">
                <div className="mb-2 flex h-[120px] w-[120px] items-center justify-center rounded-full bg-primary-300/50">
                  <USSD width={64} height={64} />
                </div>
                <p className="text-neutral-600">The USSD has expired</p>
              </div>
            )
          )}
          <div className="flex h-auto flex-col items-center">
            {isUSSDCode && ussdCode && seconds > 0 && (
              <p className="mb-12 text-sm font-medium text-neutral-600">
                The above USSD is valid for{" "}
                <span className="text-neutral-900">
                  {formatTimeForCounter(seconds)}
                </span>
              </p>
            )}
            {error && (
              <p className="mt-3 w-full text-left text-sm text-primary-300">
                {error}
              </p>
            )}
          </div>
          {isUSSDCode && ussdCode && seconds > 0 && !isConfirming && (
            <div className="flex justify-center">
              <ButtonComponent
                label="Confirm Payment"
                onClick={handleConfirmPayment}
                id="continue-button"
                size="large"
                type="primary"
                disabled={seconds <= 0 || isConfirming}
                isLoading={isConfirming}
                className="px-24"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default USSDCodePage;